import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getDisbursements, getApplications, getEMISchedule, initiateEMIPayment } from "@/api";
import { Button } from "@/components/ui/button";
import BottomNav from "@/components/BottomNav";
import { toast } from "sonner";
import { ArrowLeft, Banknote, Calendar, Loader2, ExternalLink, CheckCircle2, AlertTriangle, Smartphone } from "lucide-react";
import { openRazorpayCheckout, loadRazorpayScript } from "@/utils/razorpay";

export default function CustomerTransactions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [disbursements, setDisbursements] = useState([]);
  const [emiSchedule, setEmiSchedule] = useState([]);
  const [mandate, setMandate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(null);

  useEffect(() => { 
    loadData(); 
    // Preload Razorpay checkout script to prevent "Payment gateway not loaded" error
    loadRazorpayScript().catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const disbRes = await getDisbursements();
      setDisbursements(disbRes.data.disbursements || []);

      const appsRes = await getApplications();
      if (appsRes.data.applications && appsRes.data.applications.length > 0) {
        const firstApp = appsRes.data.applications[0];
        try {
          const emiRes = await getEMISchedule(firstApp.id);
          setEmiSchedule(emiRes.data.emi_schedule || []);
          setMandate(emiRes.data.mandate);
        } catch {}
      }
    } catch (err) {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async (emiId) => {
    setPaymentLoading(emiId);
    try {
      const res = await initiateEMIPayment(emiId);

      if (!res.data.success) {
        toast.error("Failed to create payment order");
        setPaymentLoading(null);
        return;
      }

      const { order_id, amount, amount_paise, currency, key_id, emi_id } = res.data;

      console.log('[EMI Payment] Opening Razorpay checkout for EMI:', emi_id);

      await openRazorpayCheckout({
        order_id,
        amount: amount_paise,
        currency,
        key_id,
        name: 'Rebuilt',
        description: `EMI Installment Payment - ₹${amount.toFixed(2)}`,
        prefill_contact: user?.mobile,
        prefill_email: `${user?.mobile || "user"}@rebuilt.in`,
        onSuccess: async (response) => {
          console.log('[EMI Payment] Success:', response.razorpay_payment_id);
          toast.success("EMI payment successful!");
          setPaymentLoading(null);
          loadData();
        },
        onFailure: (error) => {
          console.error('[EMI Payment] Failed:', error);
          toast.error(error.description || "Payment failed. Please try again.");
          setPaymentLoading(null);
        }
      });

    } catch (err) {
      console.error('[EMI Payment] Error:', err);
      toast.error(err.response?.data?.detail || "Payment initiation failed");
      setPaymentLoading(null);
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      pending: "bg-[#FFFBEB] text-[#F59E0B] border-[#F59E0B]/20",
      paid: "bg-[#ECFDF5] text-[#10B981] border-[#10B981]/20",
      overdue: "bg-[#FEF2F2] text-[#EF4444] border-[#EF4444]/20",
      transferred: "bg-[#ECFDF5] text-[#10B981] border-[#10B981]/20",
      approved: "bg-[#EBF3FF] text-[#1565C0] border-[#1565C0]/20"
    };
    return <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border ${styles[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{status}</span>;
  };

  const isAutopayActive = mandate?.status === 'active' && (mandate?.mandate_method === 'upi' || mandate?.razorpay_subscription_id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin w-12 h-12 border-4 border-[#1565C0]/20 border-t-[#1565C0] rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate("/customer/dashboard")} className="text-[#4B5563] hover:text-[#0A2540] transition-colors">
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <h1 className="text-lg font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>Transactions</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-6 py-8 space-y-8">
        {/* Section A: Disbursements */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-200 fade-in">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#1565C0]/10 flex items-center justify-center">
              <Banknote className="h-5 w-5 text-[#1565C0]" strokeWidth={2} />
            </div>
            <h2 className="text-xl font-bold text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>Disbursements</h2>
          </div>

          {disbursements.length === 0 ? (
            <p className="text-sm text-[#4B5563] text-center py-8 bg-gray-50 rounded-xl">No disbursements yet</p>
          ) : (
            <div className="space-y-4">
              {disbursements.map(d => (
                <div key={d.id} className="border border-gray-200 rounded-xl p-5 hover:border-[#1565C0] transition-all">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-base font-semibold text-[#0A2540]">{d.application?.lender_name || "Loan"}</p>
                      <p className="text-xs text-[#4B5563] mt-1">Amount: ₹{Number(d.application?.loan_amount || 0).toLocaleString()}</p>
                    </div>
                    <StatusBadge status={d.status} />
                  </div>
                  {d.utr_number && (
                    <div className="bg-[#EBF3FF] border border-[#1565C0]/20 rounded-lg px-3 py-2.5 mt-3">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#1565C0] font-bold mb-1">UTR Number</p>
                      <p className="text-xs font-mono font-semibold text-[#0A2540]">{d.utr_number}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section B: EMI Schedule */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-200 fade-in">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1565C0]/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-[#1565C0]" strokeWidth={2} />
              </div>
              <h2 className="text-xl font-bold text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>EMI Schedule</h2>
            </div>
          </div>

          {mandate && (
            <div className="backdrop-blur-xl bg-[#1565C0]/5 border border-[#1565C0]/20 rounded-xl p-6 mb-6">
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] font-bold mb-1">Total Repayable</p>
                  <p className="text-lg font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>₹{Number(mandate.total_repayable || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] font-bold mb-1">Installments</p>
                  <p className="text-lg font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>{mandate.installment_count} month(s)</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] font-bold mb-1">Per Installment</p>
                  <p className="text-lg font-black text-[#1565C0] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>₹{Number(mandate.per_installment_amount || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] font-bold mb-1">Payment Method</p>
                  {isAutopayActive ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold border border-emerald-200">
                      <Smartphone className="h-3 w-3" /> UPI Autopay
                    </span>
                  ) : (
                    <StatusBadge status={mandate.status} />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Autopay Active Banner */}
          {isAutopayActive && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-emerald-700">UPI Autopay Active</p>
                <p className="text-[10px] text-emerald-600 mt-0.5">EMIs will be auto-debited on due dates. Manual payment available as fallback.</p>
              </div>
            </div>
          )}

          {emiSchedule.length === 0 ? (
            <p className="text-sm text-[#4B5563] text-center py-8 bg-gray-50 rounded-xl">No EMI schedule yet</p>
          ) : (
            <div className="space-y-4">
              {emiSchedule.map(emi => (
                <div key={emi.id} className={`border rounded-xl p-5 transition-all ${
                  emi.status === 'overdue' ? 'border-red-300 bg-red-50/30' :
                  emi.status === 'paid' ? 'border-emerald-200 bg-emerald-50/30' :
                  'border-gray-200 hover:border-[#1565C0]'
                }`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <p className="text-base font-semibold text-[#0A2540]">Installment #{emi.installment_number}</p>
                      <p className="text-xs text-[#4B5563] mt-1">Due: {new Date(emi.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                      <p className="text-xl font-black text-[#1565C0] mt-2 tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>₹{Number(emi.amount).toLocaleString()}</p>
                    </div>
                    <StatusBadge status={emi.status} />
                  </div>

                  {/* Paid - Green badge */}
                  {emi.status === 'paid' && (
                    <div className="bg-[#ECFDF5] border border-[#10B981]/20 rounded-lg px-3 py-2.5 mt-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-[#10B981]" />
                        <p className="text-xs text-[#10B981] font-semibold">
                          Paid on {emi.paid_at ? new Date(emi.paid_at).toLocaleDateString('en-IN') : '—'}
                        </p>
                      </div>
                      {emi.payment_txn_id && (
                        <p className="text-xs font-mono text-[#059669] mt-1 ml-6">Txn: {emi.payment_txn_id}</p>
                      )}
                      {emi.razorpay_charge_id && emi.razorpay_charge_id !== emi.payment_txn_id && (
                        <p className="text-[10px] text-[#059669]/70 mt-0.5 ml-6">Auto-debit via UPI Autopay</p>
                      )}
                    </div>
                  )}

                  {/* Pending - fallback Pay Manually */}
                  {emi.status === 'pending' && (
                    <Button
                      onClick={() => handlePayNow(emi.id)}
                      disabled={paymentLoading === emi.id}
                      variant={isAutopayActive ? "outline" : "default"}
                      className={`w-full rounded-full py-2.5 text-xs font-semibold mt-3 transition-all shadow-sm hover:shadow hover:-translate-y-0.5 ${
                        isAutopayActive
                          ? 'border-[#1565C0] text-[#1565C0] hover:bg-[#1565C0]/5'
                          : 'bg-[#1565C0] hover:bg-[#0D47A1] text-white'
                      }`}
                    >
                      {paymentLoading === emi.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4 mr-1.5" />
                          {isAutopayActive ? 'Pay Manually' : 'Pay Now'}
                        </>
                      )}
                    </Button>
                  )}

                  {/* Overdue - Red Pay Now */}
                  {emi.status === 'overdue' && (
                    <>
                      {emi.charge_failed_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3 flex items-start gap-2">
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                          <p className="text-[10px] text-red-600">{emi.charge_failed_reason}</p>
                        </div>
                      )}
                      <Button
                        onClick={() => handlePayNow(emi.id)}
                        disabled={paymentLoading === emi.id}
                        className="w-full bg-red-600 hover:bg-red-700 text-white rounded-full py-2.5 text-xs font-semibold mt-3 transition-all shadow-sm hover:shadow hover:-translate-y-0.5"
                      >
                        {paymentLoading === emi.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <AlertTriangle className="h-4 w-4 mr-1.5" />
                            Pay Now — Overdue
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
